import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
  PendingApprovalBanner,
  useAuth,
  usePrincipal,
  visibleSurfaces,
  type Surface,
} from "@csa/web-foundation";
import { useT } from "../i18n/index";
import type { MessageKey } from "../i18n/dictionaries";
import { Icon, type IconName } from "../ui/icons";

// 04 — the admin console chrome, restructured to a persistent LEFT sidebar (parity
// with the advisor console's Fable IA). The RBAC-computed nav is grouped into FIVE
// clusters (Genel Bakış · Katalog & Fiyat · Müşteri & Pazarlama · Uyum & Yönetişim ·
// Ekip); a slim governance top-strip in the main column keeps the always-on audit
// note, the back-to-console hop, and the TR/EN toggle. The nav is still computed
// from the principal's grants via the foundation primitive — the clusters are
// PRESENTATION only; nav.ts stays the routable source of truth and the API remains
// the security boundary. DS is unchanged: only `k-` classes + existing tokens.

// Cross-app hop back to the advisor console (governance visibility, not control).
// Config, not code — defaults to a relative "/" (the console is the root deployment;
// admin is mounted under /admin) when the deployment doesn't set it.
const ADVISOR_CONSOLE_URL = import.meta.env.VITE_CONSOLE_URL ?? "/";

// The admin IA clusters. Each lists the surface KEYS it groups + an outline glyph
// from the admin icon set; a key only renders when the principal's grants make its
// surface visible, so an RBAC-hidden cluster collapses whole. Surfaces absent from
// every cluster stay routable but off the rail.
type Cluster = { key: string; labelKey: MessageKey; items: { key: string; icon: IconName }[] };
const CLUSTERS: Cluster[] = [
  {
    key: "overview",
    labelKey: "nav.section.overview",
    items: [
      { key: "dashboard", icon: "dashboard" },
      { key: "reports", icon: "scorecard" },
    ],
  },
  {
    key: "catalog",
    labelKey: "nav.section.catalog",
    items: [
      { key: "catalog", icon: "quality" },
      { key: "catalog-mgmt", icon: "records" },
      { key: "commission", icon: "tier" },
    ],
  },
  {
    key: "relations",
    labelKey: "nav.section.relations",
    items: [
      { key: "customers", icon: "household" },
      { key: "marketing", icon: "suggestion" },
    ],
  },
  {
    key: "governance",
    labelKey: "nav.section.governance",
    items: [
      { key: "dsar", icon: "lock" },
      { key: "config", icon: "vault" },
    ],
  },
  {
    key: "team",
    labelKey: "nav.section.team",
    items: [{ key: "iam", icon: "member" }],
  },
];

/** Initials for the avatar chip — first letters of up to two name words, uppercased. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const take = parts.length === 1 ? [parts[0]!] : [parts[0]!, parts[parts.length - 1]!];
  return take
    .map((p) => p[0]!.toUpperCase())
    .join("")
    .slice(0, 2);
}

export function AdminShell({
  surfaces,
  children,
}: {
  surfaces: Surface<MessageKey>[];
  children: ReactNode;
}) {
  const { me, isLoading, error } = usePrincipal();
  const { logout } = useAuth();
  const { t, locale, setLocale } = useT();

  if (isLoading) return <p className="muted">{t("principal.loading")}</p>;
  if (error || !me) return <p role="alert">{t("principal.error")}</p>;

  const visible = visibleSurfaces(me, surfaces);
  const visibleByKey = new Map(visible.map((s) => [s.key, s]));
  const clusters = CLUSTERS.map((c) => ({
    ...c,
    items: c.items
      .map((it) => ({ ...it, surface: visibleByKey.get(it.key) }))
      .filter((it): it is typeof it & { surface: (typeof visible)[number] } => Boolean(it.surface)),
  })).filter((c) => c.items.length > 0);

  const displayName = me.displayName ?? me.sub;
  const roleLabel = t(`shell.role.${me.type}` as MessageKey);

  return (
    <div className="k-app k-scope">
      <aside className="k-sidebar">
        <div className="k-sidebar__brand">
          <svg width="30" height="30" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="30" height="30" rx="9" fill="#00249E" />
            <path
              d="M8.5 16.4l4.6 4.5L23.2 10"
              stroke="#34C77B"
              strokeWidth="2.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M19.3 10H23.2V13.9"
              stroke="#34C77B"
              strokeWidth="2.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="k-sidebar__brandtext">
            <span className="k-sidebar__brandname">CSA Privé</span>
            <span className="k-sidebar__brandsub">{t("shell.section")}</span>
          </span>
        </div>

        <nav className="k-sidebar__nav" aria-label="surfaces">
          {clusters.map((group) => (
            <div key={group.key} className="k-sidebar__group">
              <div className="k-sidebar__section">{t(group.labelKey)}</div>
              {group.items.map((it) => (
                <NavLink key={it.key} to={it.surface.path} className="k-sidebar__link">
                  <Icon name={it.icon} size={19} />
                  <span>{t(it.surface.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <button
          type="button"
          className="k-sidebar__user"
          onClick={() => void logout()}
          title={t("auth.logout")}
          aria-label={t("auth.logout")}
        >
          <span className="k-sidebar__avatar">{initialsOf(displayName)}</span>
          <span className="k-sidebar__usermeta">
            <span className="k-sidebar__username">{displayName}</span>
            <span className="k-sidebar__userrole">{roleLabel}</span>
          </span>
          <Icon name="lock" size={15} className="k-sidebar__usericon" />
        </button>
      </aside>

      <div className="k-app__main">
        <header className="k-topstrip">
          <div className="k-topstrip__audit" role="note" data-testid="admin-audit-strip">
            <svg
              className="k-topstrip__auditicon"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="4"
                y="10.5"
                width="16"
                height="10"
                rx="2.5"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M8 10.5V7a4 4 0 0 1 8 0v3.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            <span className="k-topstrip__audittext">{t("shell.audit")}</span>
          </div>

          <div className="k-topstrip__right">
            <a className="k-backlink" href={ADVISOR_CONSOLE_URL} data-testid="advisor-console-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 5l-7 7 7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{t("shell.backToConsole")}</span>
            </a>
            <div className="k-langtoggle" role="group" aria-label="Language">
              <button
                type="button"
                className={`k-langtoggle__opt${locale === "tr" ? " is-active" : ""}`}
                aria-pressed={locale === "tr"}
                onClick={() => setLocale("tr")}
              >
                TR
              </button>
              <button
                type="button"
                className={`k-langtoggle__opt${locale === "en" ? " is-active" : ""}`}
                aria-pressed={locale === "en"}
                onClick={() => setLocale("en")}
              >
                EN
              </button>
            </div>
          </div>
        </header>

        <PendingApprovalBanner />

        <main className="k-app__canvas">{children}</main>
      </div>
    </div>
  );
}
